import { useJsApiLoader } from '@react-google-maps/api';
import * as Sentry from '@sentry/react';
import { t } from 'i18next';
import { useCallback, useState } from 'react';

import { GOOGLE_MAPS_LOADER_OPTIONS } from '~components/Maps/v2/constants';
import { AddressItem } from '~hooks/useAddress/models/AddressItem';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

import {
  AddressProvider,
  AddressProviderSuggestion,
  concreteAddressProviderBySupportedAddressProvider,
  SupportedAddressProvider,
} from '../useAddress/models/AddressProvider';

const GOOGLE_MAPS_PLACE_FIELDS = [
  'addressComponents',
  'displayName',
  'formattedAddress',
  'id',
  'location',
];
const GOOGLE_MAPS_PLACE_SEARCH_MAX_RESULT_COUNT = 10;

type UseGeocodeArgs = {
  addressProvider: `${SupportedAddressProvider}`;
};

export const useGeocode = (
  args: UseGeocodeArgs = { addressProvider: 'google_maps_places_text_search' },
) => {
  const [isLoading, setIsLoading] = useState(false);
  const controller = new AbortController();
  const { toasterStore } = useStores();

  const googleMapsApiLoader = useJsApiLoader(GOOGLE_MAPS_LOADER_OPTIONS);

  const createGoogleMapsGeocoderInstance = useCallback(() => {
    return new Promise<google.maps.Geocoder>((resolve, reject) => {
      if (googleMapsApiLoader.isLoaded) {
        return window.google.maps
          .importLibrary('geocoding')
          .then(() => resolve(new google.maps.Geocoder()))
          .catch(() => {
            return reject(
              new Error(`${t('error_messages.geocode.failed_to_load_google_maps_api')}`),
            );
          });
      } else if (googleMapsApiLoader.loadError) {
        reject(googleMapsApiLoader.loadError);
      }
    });
  }, [googleMapsApiLoader.isLoaded, googleMapsApiLoader.loadError]);

  const loadGoogleMapsPlacesLibrary = useCallback(() => {
    return new Promise<void>((resolve, reject) => {
      if (googleMapsApiLoader.isLoaded) {
        return window.google.maps
          .importLibrary('places')
          .then(() => resolve())
          .catch(() => {
            return reject(
              new Error(`${t('error_messages.geocode.failed_to_load_google_maps_api')}`),
            );
          });
      } else if (googleMapsApiLoader.loadError) {
        reject(googleMapsApiLoader.loadError);
      }
    });
  }, [googleMapsApiLoader.isLoaded, googleMapsApiLoader.loadError]);

  const ConcreteAddressProvider =
    concreteAddressProviderBySupportedAddressProvider[args.addressProvider];
  const addressProvider = new AddressProvider(new ConcreteAddressProvider());

  function reportPlaceLookupFailure(error: unknown) {
    toasterStore.push(
      alert(t('error_messages.geocode.failed_to_fetch_place_details'), AlertTypes.error),
    );
    Sentry.captureException(error);
  }

  async function getAddressItemByGoogleMapsPlaceId(
    placeId: string,
    fallbackDescription?: string,
  ) {
    const ConcreteAddressProvider =
      concreteAddressProviderBySupportedAddressProvider[
        SupportedAddressProvider.GOOGLE_MAPS_PLACES_TEXT_SEARCH
      ];
    const placeAddressProvider = new AddressProvider(new ConcreteAddressProvider());

    try {
      await loadGoogleMapsPlacesLibrary();

      const response = await new google.maps.places.Place({ id: placeId }).fetchFields({
        fields: GOOGLE_MAPS_PLACE_FIELDS,
      });

      // Despite its type, fetchFields resolves undefined when Google Maps is broken.
      if (!response?.place) {
        throw new Error('Google Maps returned no details for this place');
      }

      return placeAddressProvider.decodeSuggestion(response.place);
    } catch (error) {
      if (!fallbackDescription) {
        reportPlaceLookupFailure(error);
        throw error;
      }

      try {
        return await geocodeAddressDescription(fallbackDescription);
      } catch {
        reportPlaceLookupFailure(error);
        throw error;
      }
    }
  }

  async function geocodeAddressDescription(description: string): Promise<AddressItem> {
    const googleMapsGeocoder = await createGoogleMapsGeocoderInstance();
    const response = await googleMapsGeocoder.geocode({
      address: description,
    });

    if (response.results.length === 0) {
      throw new Error('Geocoding failed: No results found');
    }

    const ConcreteAddressProvider =
      concreteAddressProviderBySupportedAddressProvider[
        SupportedAddressProvider.GOOGLE_MAPS_GEOCODER
      ];
    const addressProvider = new AddressProvider(new ConcreteAddressProvider());

    return addressProvider.decodeSuggestion(response.results[0]);
  }

  const getGoogleMapsPlaces = async (params: { query: string }) => {
    if (isLoading) {
      setTimeout(() => {
        if (controller) {
          controller.abort();
        }
      }, 1);
    }

    if (!params.query?.length) {
      return;
    }

    setIsLoading(true);

    try {
      let suggestions: AddressProviderSuggestion[] = [];

      if (args.addressProvider === SupportedAddressProvider.GOOGLE_MAPS_GEOCODER) {
        const googleMapsGeocoder = await createGoogleMapsGeocoderInstance();
        const googleResponse = await googleMapsGeocoder.geocode({
          address: params.query,
          fulfillOnZeroResults: true,
        });

        suggestions = googleResponse.results;
      } else if (
        args.addressProvider === SupportedAddressProvider.GOOGLE_MAPS_PLACES_TEXT_SEARCH
      ) {
        await loadGoogleMapsPlacesLibrary();

        const googleResponse = await google.maps.places.Place.searchByText({
          textQuery: params.query,
          fields: GOOGLE_MAPS_PLACE_FIELDS,
          maxResultCount: GOOGLE_MAPS_PLACE_SEARCH_MAX_RESULT_COUNT,
        });

        suggestions = googleResponse.places;
      }

      return suggestions.map((suggestion) => {
        return addressProvider.decodeSuggestion(suggestion);
      });
    } catch (error) {
      const requestWasCanceled =
        error instanceof Object && 'code' in error && error.code === 'ERR_CANCELED';

      if (!requestWasCanceled) {
        reportRequestError(error, t('error_messages.geocode.failed_to_fetch_places'));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const encodeGoogleMapsPlace = async (coordinates: string) => {
    const [lat, lng] = coordinates.split(',').map((coord) => parseFloat(coord.trim()));

    const googleMapsGeocoder = await createGoogleMapsGeocoderInstance();
    const response = await googleMapsGeocoder.geocode({
      location: { lat, lng },
    });

    if (response.results.length) {
      const ConcreteAddressProvider =
        concreteAddressProviderBySupportedAddressProvider[
          SupportedAddressProvider.GOOGLE_MAPS_GEOCODER
        ];
      const addressProvider = new AddressProvider(new ConcreteAddressProvider());

      return addressProvider.decodeSuggestion(response.results[0]);
    }
    return {} as AddressItem;
  };

  return {
    encodeLngLat: encodeGoogleMapsPlace,
    getAddressItemByGoogleMapsPlaceId,
    getPlaces: getGoogleMapsPlaces,
  };
};
